import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {UserSubscription} from "../../../../user/user-subscription";
import {ActivatedRoute, Router} from "@angular/router";
import {DbModalService} from "../../../../modules/modal/modal";
import {BoosterService} from "../services/booster.service";
import {MigrationService} from "../services/migration.service";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {combineLatest, Subject} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../store";
import {TypeUtils} from "../../../../shared/utils/type-utils";
import {Location} from "@angular/common";
import {
    GlobalEventDispatcherService
} from "../../../../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {GoogleAnalyticsService} from "../../../../shared/services/google-analytics/google-analytics.service";
import {IBoosterStats} from "../../../../store/reducers/booster.reducer";
import {Website} from "../../website";
import {BuilderSectionsInteractionService} from "../../../../builder-v2/builder-sections-interaction.service";

@Component({
    selector: 'db-front-end',
    templateUrl: './front-end.component.html',
    styleUrls: ['./front-end.component.scss'],
    standalone: false
})
export class FrontEndComponent implements OnInit, OnDestroy, AfterViewInit {

    public currentSubscription: UserSubscription;
    public websiteId: number;
    public isMobile: boolean;
    public stats: IBoosterStats;
    public boosterModeLoading: boolean = true;
    public testModeLoading: boolean = true;
    public pageCount: string;
    public notConnected: boolean;
    private _destroyStream$: Subject<boolean> = new Subject<boolean>();
    public website: Website;
    public boosterPluginIsActive: boolean = false;
    enabledCdnCount: number;
    companyName: any;

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _location: Location,
        private _dbModal: DbModalService,
        private _migrationService: MigrationService,
        private _boosterService: BoosterService,
        private _googleAnalyticsService: GoogleAnalyticsService,
        private _store: Store<fromStore.DbStoreState>,
        private _globalEventDispatcher: GlobalEventDispatcherService,
        private _builderSectionsInteractionService: BuilderSectionsInteractionService,
    ) {
    }

    ngOnInit() {
        this._getCompanyInfo();
        if (!TypeUtils.isNull(this._route.snapshot.data['boosterTroubleshootData'])) {
            const disconnected = this._route.snapshot.data['boosterTroubleshootData'] === 'alive_but_not_connected';
            const restNoRoute = this._route.snapshot.data['boosterTroubleshootData'] === 'error';
            this.notConnected = disconnected || restNoRoute;
        }
        if (window.innerWidth < 991) {
            this.isMobile = true;
        }
        this._initData();
        const queryParams: any = (<any>this._route.queryParams).value;
        const fromExploreDialog = queryParams.fromExploreDialog || false;
        if (!!fromExploreDialog) {
            this.testModeLoading = false;
            this.boosterModeLoading = false;
        }
    }

    private _getCompanyInfo(): void {
        this._builderSectionsInteractionService.companyInfo
            .pipe(
                filter(companyInfo => !!companyInfo),
                takeUntil(this._destroyStream$),
                first()
            )
            .subscribe(companyInfo => {
                this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
            });
    }

    ngOnDestroy() {
        this._destroyStream$.next(true);
        this._destroyStream$.complete();
    }

    private _initData(): void {
        this.website = this._route.snapshot.parent.parent.data['website'];
        this.websiteId = this._route.snapshot.parent.parent.data['website'].id;
        this._getWebsiteBoosterStats();
        this._getUserSubscription();
        this._checkBoosterState();
        this._getEnableCDNCount();
    }

    private _getEnableCDNCount(): void {
        this._store.select(fromStore.getWebsitesLoaded)
            .pipe(
                filter((loaded: boolean) => loaded),
                switchMap(() =>
                    this._store.select(fromStore.getWebsites),
                ),
                takeUntil(this._destroyStream$)
            )
            .subscribe((websites) => {
                this.enabledCdnCount = websites.filter((_website: Website) => !!_website.cloudflareEnabled).length;
            });
    }

    private _getUserSubscription(): void {
        this._store.select(fromStore.getUserLoaded)
            .pipe(
                filter((loaded: boolean) => loaded),
                switchMap(() => combineLatest(
                    this._store.select(fromStore.getUserSubscription)
                )),
                takeUntil(this._destroyStream$)
            )
            .subscribe(([currentSubscription]) => {
                this.currentSubscription = currentSubscription;
            });
    }

    ngAfterViewInit() {
        const queryParams: any = (<any>this._route.queryParams).value;
        const pricing = queryParams.pricing;
        const scoreBlock = queryParams.score;
        const queryParamsIndex: number = this._router.url.indexOf('?');

        if (queryParamsIndex !== -1) {
            this._location.replaceState(this._router.url.substring(0, queryParamsIndex));
        }

        if (scoreBlock) {
            setTimeout(() => {
                const scrollTop = document.getElementsByClassName('db-booster-page-score') && document.getElementsByClassName('db-booster-page-score')[0] as HTMLElement | null;
                this._goToElement(scrollTop);
            }, 200);
        }

        if (pricing) {
            setTimeout(() => {
                const scrollTop = document.getElementsByClassName('db-booster-features-list') && document.getElementsByClassName('db-booster-features-list')[0] as HTMLElement | null;
                this._goToElement(scrollTop);
            }, 200);
        }
    }

    private _goToElement(scrollTop): void {
        const container = document.getElementsByTagName("db-front-end") && document.getElementsByTagName("db-front-end")[0];
        scrollTop && container.scrollTo(
            {
                top: scrollTop.offsetTop,
                behavior: 'smooth'
            });
    }

    private _getWebsiteBoosterStats(): void {
        this._store.select(fromStore.getWebsiteBoosterStats(this.websiteId))
            .pipe(
                takeUntil(this._destroyStream$)
            )
            .subscribe((stats): void => {
                if (!TypeUtils.isNull(stats)) {
                    this.stats = stats;
                }
            });
    }

    private _checkBoosterState(): void {
        this._store.select(fromStore.getWebsitePluginsLoaded(this.website?.id))
            .pipe(
                filter((loaded: boolean) => loaded),
                switchMap(() =>
                    this._store.select(fromStore.getWebsiteOptimizerPlugin(this.website?.id)),
                ),
                takeUntil(this._destroyStream$)
            )
            .subscribe((optimizerPlugin): void => {
                if (optimizerPlugin && !TypeUtils.isEmpty(optimizerPlugin)) {
                    this.boosterPluginIsActive = optimizerPlugin.active;
                }
            });
    }

}
